import type { GroupScheduleFormSchema } from '@repo/schemas/group/groupSchedule';
import type { LocationPublic } from '@repo/types/locations';
import type { UseFieldArrayRemove, UseFormReturn } from 'react-hook-form';

import { weekStartsOnMonday } from '@repo/lib/constants/calendar-constants';
import { addDays, format, parse, startOfWeek } from 'date-fns';
import { Trash2Icon, MinusIcon } from 'lucide-react';
import { useWatch } from 'react-hook-form';

import { MeetingTypeFormField } from '@/components/features/meeting/MeetingTypeFormField';
import TimeSelect from '@/components/features/time-range-select/TimeSelect';
import { AccordionContent, AccordionItem, AccordionTrigger } from '@/components/ui/accordion';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Checkbox } from '@/components/ui/checkbox';
import { FormControl, FormField, FormLabel, FormItem, FormMessage, useErrorMessage } from '@/components/ui/form';
import { Input } from '@/components/ui/input';
import { Select, SelectContent, SelectGroup, SelectItem, SelectLabel, SelectTrigger, SelectValue } from '@/components/ui/select';
import { DEFAULT_TIME_STEP } from '@/utils/calendar';
import { REPEAT_LABELS } from '@/utils/schedule';

interface GroupScheduleMeetingFormCardProps {
  form: UseFormReturn<GroupScheduleFormSchema>;
  index: number;
  remove: UseFieldArrayRemove;
  locations: { data: Array<LocationPublic>; isPending: boolean };
}

function formatDayAndTimeLabel(day?: number, startT?: string, endT?: string) {
  if (!day || !startT || !endT) return '--';
  const dayBase = addDays(startOfWeek(new Date(), { weekStartsOn: weekStartsOnMonday }), day - 1);
  const start = parse(startT, 'HH:mm', dayBase);
  const end = parse(endT, 'HH:mm', dayBase);
  return `${format(start, 'EEE HH:mm')} - ${format(end, 'HH:mm')}`;
}

export function GroupScheduleMeetingFormCard(groupScheduleMeetingFormCardProps: GroupScheduleMeetingFormCardProps) {
  const {
    form,
    index,
    remove,
    locations: { data: locations, isPending },
  } = groupScheduleMeetingFormCardProps;

  const meeting = useWatch({
    control: form.control,
    name: `meetings.${index}`,
  });
  const { type, repeat, startT, day, endT } = meeting;
  const typeFieldName: `meetings.${number}.type` = `meetings.${index}.type`;
  const meetingURLFieldName: `meetings.${number}.meetingURL` = `meetings.${index}.meetingURL`;
  const locationFieldName: `meetings.${number}.location` = `meetings.${index}.location`;

  const { error: nameError } = useErrorMessage(`meetings.${index}.name`);
  const { error: startError } = useErrorMessage(`meetings.${index}.startT`);
  const { error: endError } = useErrorMessage(`meetings.${index}.endT`);
  const { error: locationError } = useErrorMessage(`meetings.${index}.location`);
  const { error: meetingUrlError } = useErrorMessage(`meetings.${index}.meetingURL`);

  const isError = Boolean(nameError || startError || endError || locationError || meetingUrlError);

  return (
    <AccordionItem
      value={`meeting.${index}`}
      aria-invalid={isError}
      className={'border-border-primary bg-card/35 aria-invalid:border-border-destructive/75 aria-invalid:ring-focus-ring-destructive/50 overflow-hidden rounded-md border aria-invalid:ring-3'}
    >
      <AccordionTrigger className={'hover:bg-muted/75 focus-visible:bg-muted items-center gap-2 rounded-t-md rounded-b-none border-0 p-2 transition-colors duration-150 hover:no-underline'}>
        <div className={'order-first flex flex-1 flex-wrap gap-1 overflow-hidden'}>
          <Badge variant={'muted'} border size={'sm'}>
            {formatDayAndTimeLabel(day, startT, endT)}
          </Badge>
          <Badge variant={'muted'} border size={'sm'}>
            {type === 'remote' ? 'Zdalnie' : 'Stacjonarnie'}
          </Badge>
          <Badge variant={'muted'} border size={'sm'}>
            {REPEAT_LABELS[repeat ?? 'weekly']}
          </Badge>
        </div>

        <Button
          asChild
          type={'button'}
          size={'sm'}
          variant={'ghost'}
          variantColor={'destructive'}
          className={'order-last ml-1'}
          onClick={(event) => {
            event.stopPropagation();
            remove(index);
          }}
        >
          <span>
            <Trash2Icon />
          </span>
        </Button>
      </AccordionTrigger>

      <AccordionContent className={'flex flex-col gap-3 border-t p-4'}>
        <FormField
          name={`meetings.${index}.name`}
          control={form.control}
          render={({ field }) => (
            <FormItem>
              <FormLabel>Nazwa</FormLabel>
              <FormControl>
                <Input {...field} placeholder={'Nazwa spotkania'} />
              </FormControl>
              <FormMessage />
            </FormItem>
          )}
        />
        <div className="flex flex-col gap-3">
          <FormField
            name={`meetings.${index}.day`}
            control={form.control}
            render={({ field }) => (
              <FormItem>
                <FormLabel>Dzień tygodnia</FormLabel>
                <Select value={field.value ? String(field.value) : ''} onValueChange={(value) => field.onChange(Number(value))}>
                  <FormControl>
                    <SelectTrigger>
                      <SelectValue placeholder="Wybierz dzień" />
                    </SelectTrigger>
                  </FormControl>
                  <SelectContent>
                    <SelectGroup>
                      <SelectLabel>Dzień tygodnia</SelectLabel>
                      <SelectItem value={'1'}>Poniedziałek</SelectItem>
                      <SelectItem value={'2'}>Wtorek</SelectItem>
                      <SelectItem value={'3'}>Środa</SelectItem>
                      <SelectItem value={'4'}>Czwartek</SelectItem>
                      <SelectItem value={'5'}>Piątek</SelectItem>
                      <SelectItem value={'6'}>Sobota</SelectItem>
                      <SelectItem value={'7'}>Niedziela</SelectItem>
                    </SelectGroup>
                  </SelectContent>
                </Select>
                <FormMessage />
              </FormItem>
            )}
          />
          <fieldset>
            <FormLabel asChild>
              <legend>Godziny</legend>
            </FormLabel>
            <div className={'mt-1.5 flex gap-1.5'}>
              <FormField
                name={`meetings.${index}.startT`}
                control={form.control}
                rules={{ deps: [`meetings.${index}.endT`] }}
                render={({ field }) => (
                  <FormItem className={'w-full'}>
                    <FormControl>
                      <TimeSelect
                        value={field.value ? parse(field.value, 'HH:mm', new Date()) : null}
                        onValueChange={(dateTime) => {
                          field.onChange(format(dateTime, 'HH:mm'));
                          field.onBlur();
                        }}
                        className={'w-full'}
                        minutesStep={DEFAULT_TIME_STEP}
                      />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />
              <span className={'text-muted-foreground flex h-10 shrink-0 items-center justify-center'}>
                <MinusIcon className={'size-3'} aria-hidden="true" />
              </span>
              <FormField
                name={`meetings.${index}.endT`}
                control={form.control}
                rules={{ deps: [`meetings.${index}.startT`] }}
                render={({ field }) => (
                  <FormItem className={'w-full'}>
                    <FormControl>
                      <TimeSelect
                        value={field.value ? parse(field.value, 'HH:mm', new Date()) : null}
                        onValueChange={(dateTime) => {
                          field.onChange(format(dateTime, 'HH:mm'));
                          field.onBlur();
                        }}
                        className={'w-full'}
                        minutesStep={DEFAULT_TIME_STEP}
                      />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />
            </div>
          </fieldset>
        </div>

        <FormField
          name={`meetings.${index}.repeat`}
          control={form.control}
          render={({ field }) => (
            <FormItem>
              <FormLabel>Powtarzaj co</FormLabel>
              <Select value={field.value ?? 'weekly'} onValueChange={field.onChange}>
                <FormControl>
                  <SelectTrigger>
                    <SelectValue placeholder={'Wybierz częstotliwość'} />
                  </SelectTrigger>
                </FormControl>
                <SelectContent>
                  <SelectGroup>
                    <SelectLabel>Częstotliwość</SelectLabel>
                    {Object.entries(REPEAT_LABELS).map(([value, label]) => (
                      <SelectItem key={value} value={value}>
                        {label}
                      </SelectItem>
                    ))}
                  </SelectGroup>
                </SelectContent>
              </Select>
              <FormMessage />
            </FormItem>
          )}
        />

        <FormField
          name={`meetings.${index}.skipHolidays`}
          control={form.control}
          render={({ field }) => (
            <FormItem className={'grid grid-cols-[auto_1fr] items-start gap-x-2 gap-y-1'}>
              <FormControl>
                <Checkbox
                  className={'mt-0.5'}
                  checked={field.value ?? false}
                  onCheckedChange={(checked) => {
                    field.onChange(checked === true);
                    field.onBlur();
                  }}
                />
              </FormControl>
              <div className={'grid gap-1'}>
                <FormLabel>Pomijaj dni wolne</FormLabel>
                <p className={'text-muted-foreground text-xs'}>Nie twórz spotkań wypadających w okresie dni wolnych.</p>
              </div>
              <FormMessage className={'col-start-2'} />
            </FormItem>
          )}
        />

        <MeetingTypeFormField<GroupScheduleFormSchema, `meetings.${number}.type`, `meetings.${number}.meetingURL`, `meetings.${number}.location`>
          form={form}
          typeName={typeFieldName}
          meetingURLName={meetingURLFieldName}
          locationName={locationFieldName}
          locations={locations}
          isLocationsPending={isPending}
        />
      </AccordionContent>
    </AccordionItem>
  );
}
